import { useState, useEffect } from 'react';
import { X, Send, CheckCircle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ConsentCheckbox, PrivacyPolicyLink, usePrivacyConsent } from './PrivacyConsent';

const RequestModal = ({ isOpen, onClose, planName, planPrice }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { hasConsent, setHasConsent, consentError, validateConsent, saveConsent } = usePrivacyConsent();

  const getDefaultMessage = () => {
    let message = `Заявка на AI автоматизацию:\n\n`;
    message += `Выбранный тариф: ${planName}\n`;
    message += `Стоимость: $${planPrice}/month\n\n`;
    message += `Клиент: ${formData.name || '[Имя не указано]'}\n`;
    message += `Компания: ${formData.company || '[Не указана]'}\n`;
    message += `Email: ${formData.email || '[Не указан]'}\n`;
    message += `Телефон: ${formData.phone || '[Не указан]'}\n\n`;
    message += `Дополнительная информация:\n${formData.message || '[Нет]'}\n\n`;
    message += `---\n`;
    message += `Согласие на обработку ПД: ${hasConsent ? 'Да' : 'Нет'}\n`;
    message += `User-Agent: ${navigator.userAgent || 'Не определен'}\n`;
    message += `Время отправки: ${new Date().toLocaleString()}\n\n`;
    message += `---\nОтправлено с сайта combi.kz/automation`;
    return message;
  };

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateConsent(hasConsent)) {
      return;
    }
    
    setIsSubmitting(true);

    try {
      saveConsent(formData.email, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        planName,
        planPrice,
        consentGiven: true
      });

      const requests = JSON.parse(localStorage.getItem('form_requests') || '{}');
      if (!requests[formData.email]) requests[formData.email] = [];
      requests[formData.email].push({
        planName,
        planPrice,
        timestamp: new Date().toISOString(),
        data: formData
      });
      localStorage.setItem('form_requests', JSON.stringify(requests));

      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('company', formData.company);
      formDataToSend.append('message', getDefaultMessage());
      formDataToSend.append('_subject', `Заявка на автоматизацию: ${planName}`);
      formDataToSend.append('_template', 'table');
      formDataToSend.append('_consent', 'true');
      
      const response = await fetch('https://formsubmit.co/khamit@combi.kz', {
        method: 'POST',
        body: formDataToSend
      });
      
      if (response.ok) {
        setIsSubmitting(false);
        setIsSuccess(true);
        setTimeout(() => {
          setIsSuccess(false);
          onClose();
          setFormData({ name: '', email: '', phone: '', company: '', message: '' });
          setHasConsent(false);
        }, 2000);
      } else {
        throw new Error('Ошибка отправки');
      }
    } catch (error) {
      console.error('Error:', error);
      setIsSubmitting(false);
      alert('Ошибка отправки. Пожалуйста, попробуйте еще раз или напишите нам на почту khamit@combi.kz');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-fg/70 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-800 rounded-2xl shadow-2xl animate-modal-slide-in">
        <div className="sticky top-0 z-10 flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-primary to-secondary">
          <h2 className="text-2xl font-bold text-white">{t('requestTitle')}</h2>
          <button onClick={onClose} className="text-white hover:opacity-80 transition-opacity">
            <X size={24} />
          </button>
        </div>

        <div className="p-4 bg-primary/5 border-b border-primary/10">
          <p className="text-sm text-gray-600 dark:text-gray-300">{t('selectedPlan')}</p>
          <p className="font-semibold text-primary">
            {planName} - ${planPrice}/month
          </p>
        </div>

        {isSuccess ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-primary" />
            </div>
            <h3 className="text-xl font-semibold text-primary mb-2">
              {t('requestSuccess')}
            </h3>
            <p className="text-gray-600 dark:text-gray-300">{t('requestSuccessDesc')}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('formName')} <span className="text-accent">*</span>
              </label>
              <input
                type="text"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition"
                placeholder={t('formNamePlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('formEmail')} <span className="text-accent">*</span>
              </label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition"
                placeholder="client@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('formPhone')}
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition"
                placeholder="+7 777 777 77 77"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('formCompany')}
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition"
                placeholder={t('formCompanyPlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('formMessage')}
              </label>
              <textarea
                name="message"
                rows="3"
                value={formData.message}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition resize-none"
                placeholder={t('formMessagePlaceholder')}
              />
            </div>

            <div className="pt-2">
              <ConsentCheckbox 
                onConsentChange={setHasConsent}
                required={true}
              />
              {consentError && (
                <p className="text-xs text-accent mt-1">{consentError}</p>
              )}
            </div>

            <div className="flex justify-start">
              <PrivacyPolicyLink>
                {t('readPrivacyPolicy')}
              </PrivacyPolicyLink>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 bg-gradient-to-r from-primary to-secondary text-white rounded-xl font-semibold hover:shadow-lg transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  {t('sending')}
                </>
              ) : (
                <>
                  <Send size={20} />
                  {t('sendRequest')}
                </>
              )}
            </button>

            <p className="text-xs text-center text-gray-500 dark:text-gray-400 pb-2">
              {t('formNote')}
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default RequestModal;